import React, { useRef, useState, useEffect, useMemo, useCallback } from "react";
import clsx from "clsx";
import Typography from "@material-ui/core/Typography";
import useStyles from "./SelectOrderType.style.js";
import {
  Button,
  Divider,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  Select,
  MenuItem,
} from "@material-ui/core";
import DateSlotPicker from "../../Checkout/DateSlotPicker/index.js";
import TimePicker from "../../Checkout/TimePicker/index.js";
import { ORDER_TYPES } from "../../../utils/Constants.js";
import useTimeServices from "../../../CustomHooks/useTimeServices";
import useScheduledSlots from "../../../CustomHooks/useScheduledSlots";
import { saveCheckoutForm } from "../../../store/Cart/CartActions.js";
import useCheckoutForm from "../../../CustomHooks/useCheckoutForm.js";
import { DateTime } from "luxon";
import { dispatchErrorMessageAsWarning } from "../../../utils/Shared.js";
import { useDispatch } from "react-redux";
import SpecialAlert from "../../SpecialAlert/SpecialAlert";

const SelectOrderTypeUI = ({
  orderTypes = [],
  selectedOrderType = null,
  hotel,
  dateSlots = [],
  timeSlots = [],
  showNoSlots = false,
  showOrderTime = true,
  onOrderTypeClick = () => {},
  onContinueClick = () => {},
  onDateAndTimeSelect = () => {},
}) => {
  const classes = useStyles();
  const renderCount = useRef(0);
  const dispatch = useDispatch();
  renderCount.current += 1;



  const { isAsapAvailable } = useTimeServices();
  const { 
    getAvailableDates, 
    getTimeSlots, 
    isScheduledAvailable, 
    isTodayHoliday,
    getBufferTime,
    isOrderTypeAvailableForDate
  } = useScheduledSlots();

  // Internal state for order time type
  const [orderTimeType, setOrderTimeType] = useState("ASAP");
  
  // State for scheduled functionality
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTime, setSelectedTime] = useState(null);
  const [availableDates, setAvailableDates] = useState([]);
  const [availableTimeSlots, setAvailableTimeSlots] = useState([]);
  const { formValues, setFormValues } = useCheckoutForm();

  // Handle time selection - moved before useEffects that use it
  const handleTimeChange = useCallback((timeSlot) => {
    if (timeSlot && timeSlot.value && selectedOrderType) {
    
      
      // Set the selected time in local state only
      setSelectedTime(timeSlot);
      
      // Don't call onDateAndTimeSelect here - wait for Continue button
      // This fixes the first-click issue by only updating local state
    } 
  }, [selectedOrderType]);

  // Helper function to get time slots filtered by order type and ensure first slot for non-today dates
  const getFilteredTimeSlots = useCallback((date, orderType) => {
    if (!date || !orderType) return [];
    
    const timeSlots = getTimeSlots(date, orderType);
    
    if (!timeSlots || timeSlots.length === 0) return [];
    
    // For today, filter by current time (this is handled by the hook)
    // For other dates, show all available slots starting from the first one
    const isToday = DateTime.fromFormat(date, "LL").hasSame(DateTime.now(), 'day');
    
    if (isToday) {
      // For today, the hook already filters by current time + buffer
      return timeSlots;
    } else {
      // For other dates, show all slots starting from the first available
      return timeSlots;
    }
  }, [getTimeSlots]);

  // useEffect(() => {
  //   if(orderTimeType){
  //     setFormValues({
  //       ...formValues,
  //       deliveryType:orderTimeType
  //     });
  //   }
  // }, [orderTimeType])

  // Generate dates and time slots when scheduled is selected - consolidated into one effect
  useEffect(() => {
    if (orderTimeType === "SCHEDULED" && selectedOrderType) {
      const allDates = getAvailableDates; // ✅ Fixed: Use as property, not function
      
      // Filter dates to only include those that have valid slots for the selected order type
      const validDates = allDates.filter(date => {
        // Use the enhanced validation function to check if this order type is available for this date
        return isOrderTypeAvailableForDate(date.date, selectedOrderType);
      });
      
      // Only update if dates actually changed
      if (JSON.stringify(validDates) !== JSON.stringify(availableDates)) {
        setAvailableDates(validDates);
        
        // Check if today has no slots and auto-select next available date
        if (validDates && validDates.length > 0) {
          let dateToSelect = validDates[0]; // Default to first available date
          let foundValidDate = false;
          
          // Check if today has no time slots OR if today is a holiday
          const todayDate = validDates.find(d => d.label === "Today");
          if (todayDate) {
            const todayTimeSlots = getFilteredTimeSlots(todayDate.date, selectedOrderType);
            
            if (!todayTimeSlots || todayTimeSlots.length === 0) {
              // Today has no slots, find next available date
              for (let i = 1; i < validDates.length; i++) {
                const nextDate = validDates[i];
                const nextDateTimeSlots = getFilteredTimeSlots(nextDate.date, selectedOrderType);
                if (nextDateTimeSlots && nextDateTimeSlots.length > 0) {
                  dateToSelect = nextDate;
                  foundValidDate = true;
                  break;
                }
              }
            } else {
              foundValidDate = true;
            }
          } else {
            // No "Today" label found (likely because today is a holiday), 
            // check first available date
            const firstDateTimeSlots = getFilteredTimeSlots(validDates[0].date, selectedOrderType);
            if (firstDateTimeSlots && firstDateTimeSlots.length > 0) {
              foundValidDate = true;
            }
          }
          
          // If no valid date found with slots, don't proceed
          if (!foundValidDate) {
            return; // Don't proceed with setting dates/times
          }
          
          // Set the selected date
          setSelectedDate(dateToSelect.date);
          
          // Also auto-select first time slot for this date
          const timeSlots = getFilteredTimeSlots(dateToSelect.date, selectedOrderType);
          
          if (timeSlots && timeSlots.length > 0) {
            setSelectedTime(timeSlots[0]);
            setAvailableTimeSlots(timeSlots);
            // Don't call callback here - wait for Continue button
          }
        } else {
          // No dates available at all - don't show warning here, handled in centralized useEffect
        }
      } else {
      }
    } else {
      // Only clear if not already empty
      if (availableDates.length > 0 || selectedDate || availableTimeSlots.length > 0) {
        setAvailableDates([]);
        setSelectedDate(null);
        setSelectedTime(null);
        setAvailableTimeSlots([]);
      }
    }
  }, [orderTimeType, selectedOrderType, getAvailableDates, getFilteredTimeSlots, availableDates, isOrderTypeAvailableForDate]); // Removed unnecessary dependencies

  // Update time slots when date changes and auto-select first slot
  useEffect(() => {
    if (orderTimeType === "SCHEDULED" && selectedDate && selectedOrderType) {
      const timeSlots = getFilteredTimeSlots(selectedDate, selectedOrderType);
      setAvailableTimeSlots(timeSlots);
      
      // Auto-select first time slot when date changes
      if (timeSlots && timeSlots.length > 0) {
        setSelectedTime(timeSlots[0]);
        // Don't call callback here - wait for Continue button
      } else {
        // Current date has no slots, find next available date
        const dates = getAvailableDates;
        if (dates && dates.length > 0) {
          const currentDateIndex = dates.findIndex(d => d.date === selectedDate);
          if (currentDateIndex !== -1) {
            // Find next available date with slots
            for (let i = currentDateIndex + 1; i < dates.length; i++) {
              const nextDate = dates[i];
              const nextDateTimeSlots = getFilteredTimeSlots(nextDate.date, selectedOrderType);
              if (nextDateTimeSlots && nextDateTimeSlots.length > 0) {
                setSelectedDate(nextDate.date);
                setSelectedTime(nextDateTimeSlots[0]);
                setAvailableTimeSlots(nextDateTimeSlots);
                return; // Found next available date
              }
            }
          }
        }
        
        // If no next available date found, don't show warning here, handled in centralized useEffect
      }
    }
  }, [selectedDate, selectedOrderType, getFilteredTimeSlots, getAvailableDates, setSelectedDate, setSelectedTime, setAvailableTimeSlots]); // Removed dispatchErrorMessageAsWarning dependency

  // Determine ASAP label based on order type
  const asapLabel = useMemo(() => {
    if (selectedOrderType === ORDER_TYPES.DELIVERY) {
      return isAsapAvailable(ORDER_TYPES.DELIVERY)
        ? { available: true, label: `ASAP: in ${getBufferTime(ORDER_TYPES.DELIVERY)}-${getBufferTime(ORDER_TYPES.DELIVERY) + 5} mins` }
        : { available: false, label: "ASAP : Not Available" };
    }
    if (selectedOrderType === ORDER_TYPES.PICKUP) {
      return isAsapAvailable(ORDER_TYPES.PICKUP)
        ? { available: true, label: `ASAP: in ${getBufferTime(ORDER_TYPES.PICKUP)}-${getBufferTime(ORDER_TYPES.PICKUP) + 5} mins` }
        : { available: false, label: "ASAP : Not Available" };
    }
    return { available: false, label: "ASAP : Not Available" };
  }, [selectedOrderType, isAsapAvailable, getBufferTime]);

  // Check if scheduled is available (has dates AND time slots)
  const scheduledAvailable = useMemo(() => {
    if (!isScheduledAvailable || !selectedOrderType) {
      return false;
    }
    
    // Always use the full available dates from the hook, not just the local state
    const dates = getAvailableDates; // ✅ Fixed: Use as property, not function
    
    if (!dates || dates.length === 0) {
      return false;
    }
    
    // Check if any of the available dates have valid time slots for the selected order type
    let hasValidSlots = false;
    for (const date of dates) {
      // Use the new function to check if order type is available for this date
      if (isOrderTypeAvailableForDate(date.date, selectedOrderType)) {
        hasValidSlots = true;
        break; // Found at least one date with valid slots
      }
    }
    
    return hasValidSlots;
  }, [isScheduledAvailable, selectedOrderType, isOrderTypeAvailableForDate, getAvailableDates]);

  // Initialize order time type only on component mount
  useEffect(() => {
    if (!selectedOrderType) return;
    
    // Only set default if orderTimeType hasn't been set yet (initial render)
    if (orderTimeType !== "ASAP") return; // Already set, don't change
    
    // Always start with ASAP if available
    if (asapLabel.available) {
      setOrderTimeType("ASAP");
      // Don't call callback here - wait for Continue button
    } else if (scheduledAvailable) {
      // Only switch to SCHEDULED if ASAP is not available but SCHEDULED is
      setOrderTimeType("SCHEDULED");
      // Don't call callback here - wait for Continue button
      
      // When switching to SCHEDULED, check if today has no slots and auto-select next available date
      const allDates = getAvailableDates;
      if (allDates && allDates.length > 0) {
        // Filter dates to only include those that have valid slots for the selected order type
        const validDates = allDates.filter(date => {
          return isOrderTypeAvailableForDate(date.date, selectedOrderType);
        });
        
        if (validDates.length === 0) {
          return; // No valid dates for this order type
        }
        
        let dateToSelect = validDates[0]; // Default to first available date
        let foundValidDate = false;
        
        // Check if today has no time slots OR if today is a holiday
        const todayDate = validDates.find(d => d.label === "Today");
        if (todayDate) {
          const todayTimeSlots = getTimeSlots(todayDate.date, selectedOrderType);
          if (!todayTimeSlots || todayTimeSlots.length === 0) {
            // Today has no slots, find next available date
            for (let i = 1; i < validDates.length; i++) {
              const nextDate = validDates[i];
              const nextDateTimeSlots = getTimeSlots(nextDate.date, selectedOrderType);
              if (nextDateTimeSlots && nextDateTimeSlots.length > 0) {
                dateToSelect = nextDate;
                foundValidDate = true;
                break;
              }
            }
          } else {
            foundValidDate = true;
          }
        } else {
          // No "Today" label found (likely because today is a holiday), 
          // check first available date
          const firstDateTimeSlots = getFilteredTimeSlots(validDates[0].date, selectedOrderType);
          if (firstDateTimeSlots && firstDateTimeSlots.length > 0) {
            foundValidDate = true;
          }
        }
        
        // If no valid date found with slots, don't proceed
        if (!foundValidDate) {
          return; // Don't proceed with setting dates/times
        }
        
        // Set the selected date
        setSelectedDate(dateToSelect.date);
        
        // Also auto-select first time slot for this date
        const timeSlots = getFilteredTimeSlots(dateToSelect.date, selectedOrderType);
        if (timeSlots && timeSlots.length > 0) {
          setSelectedTime(timeSlots[0]);
          setAvailableTimeSlots(timeSlots);
        }
      } else {
        // No dates available at all - don't show warning here, handled in useEffect
      }
    }
  }, [selectedOrderType, asapLabel.available, scheduledAvailable, getAvailableDates, getFilteredTimeSlots, getTimeSlots, orderTimeType, isOrderTypeAvailableForDate]); // Added missing dependencies

  // Auto-switch to ASAP if SCHEDULED becomes unavailable
  useEffect(() => {
    if (!scheduledAvailable && orderTimeType === "SCHEDULED" && asapLabel.available) {
      setOrderTimeType("ASAP");
      // Don't call callback here - wait for Continue button
    }
    
    // Check if no dates are available for scheduled orders - don't show warning here, handled in centralized useEffect
    if (orderTimeType === "SCHEDULED" && selectedOrderType) {
      const dates = getAvailableDates;
      if (dates && dates.length === 0) {
        // No dates available - handled in centralized useEffect
      }
    }
  }, [scheduledAvailable, orderTimeType, asapLabel.available, selectedOrderType, getAvailableDates, dispatchErrorMessageAsWarning]);

  // Handle order type changes - reset time-related state and re-initialize
  useEffect(() => {
    if (selectedOrderType) {
      // Clear time-related state when order type changes
      setSelectedTime(null);
      setSelectedDate(null);
      setAvailableTimeSlots([]);
      
      // Reset to ASAP if available for the new order type
      if (asapLabel.available) {
        setOrderTimeType("ASAP");
      } else if (scheduledAvailable) {
        setOrderTimeType("SCHEDULED");
        
        // Initialize scheduled selection for the new order type
        const allDates = getAvailableDates;
        if (allDates && allDates.length > 0) {
          // Filter dates to only include those that have valid slots for the selected order type
          const validDates = allDates.filter(date => {
            return isOrderTypeAvailableForDate(date.date, selectedOrderType);
          });
          
          if (validDates.length === 0) {
            return; // No valid dates for this order type
          }
          
          let dateToSelect = validDates[0]; // Default to first available date
          let foundValidDate = false;
          
          // Check if today has no time slots OR if today is a holiday
          const todayDate = validDates.find(d => d.label === "Today");
          if (todayDate) {
            const todayTimeSlots = getFilteredTimeSlots(todayDate.date, selectedOrderType);
            if (!todayTimeSlots || todayTimeSlots.length === 0) {
              // Today has no slots, find next available date
              for (let i = 1; i < validDates.length; i++) {
                const nextDate = validDates[i];
                const nextDateTimeSlots = getFilteredTimeSlots(nextDate.date, selectedOrderType);
                if (nextDateTimeSlots && nextDateTimeSlots.length > 0) {
                  dateToSelect = nextDate;
                  foundValidDate = true;
                  break;
                }
              }
            } else {
              foundValidDate = true;
            }
          } else {
            // No "Today" label found (likely because today is a holiday), 
            // check first available date
            const firstDateTimeSlots = getFilteredTimeSlots(validDates[0].date, selectedOrderType);
            if (firstDateTimeSlots && firstDateTimeSlots.length > 0) {
              foundValidDate = true;
            }
          }
          
          // If no valid date found with slots, don't proceed
          if (!foundValidDate) {
            return; // Don't proceed with setting dates/times
          }
          
          // Set the selected date
          setSelectedDate(dateToSelect.date);
          
          // Also auto-select first time slot for this date
          const timeSlots = getFilteredTimeSlots(dateToSelect.date, selectedOrderType);
          if (timeSlots && timeSlots.length > 0) {
            setSelectedTime(timeSlots[0]);
            setAvailableTimeSlots(timeSlots);
          }
        } else {
          // No dates available at all - don't show warning here, handled in centralized useEffect
        }
      }
    }
  }, [selectedOrderType, asapLabel.available, scheduledAvailable, getAvailableDates, getFilteredTimeSlots, isOrderTypeAvailableForDate]);

  // Handle order type changes from parent component
  useEffect(() => {
    if (selectedOrderType) {
      // Clear time-related state when order type changes
      setSelectedTime(null);
      setSelectedDate(null);
      setAvailableTimeSlots([]);
      
      // Re-initialize based on new order type
      if (asapLabel.available) {
        setOrderTimeType("ASAP");
      } else if (scheduledAvailable) {
        setOrderTimeType("SCHEDULED");
        
        // Initialize scheduled selection for the new order type
        const allDates = getAvailableDates;
        if (allDates && allDates.length > 0) {
          // Filter dates to only include those that have valid slots for the selected order type
          const validDates = allDates.filter(date => {
            return isOrderTypeAvailableForDate(date.date, selectedOrderType);
          });
          
          if (validDates.length === 0) {
            dispatchErrorMessageAsWarning({
              title: "Warning",
              message: "No slots for scheduled"
            });
            return; // No valid dates for this order type
          }
          
          let dateToSelect = validDates[0]; // Default to first available date
          let foundValidDate = false;
          
          // Check if today has no time slots OR if today is a holiday
          const todayDate = validDates.find(d => d.label === "Today");
          if (todayDate) {
            const todayTimeSlots = getFilteredTimeSlots(todayDate.date, selectedOrderType);
            if (!todayTimeSlots || todayTimeSlots.length === 0) {
              // Today has no slots, find next available date
              for (let i = 1; i < validDates.length; i++) {
                const nextDate = validDates[i];
                const nextDateTimeSlots = getFilteredTimeSlots(nextDate.date, selectedOrderType);
                if (nextDateTimeSlots && nextDateTimeSlots.length > 0) {
                  dateToSelect = nextDate;
                  foundValidDate = true;
                  break;
                }
              }
            } else {
              foundValidDate = true;
            }
          } else {
            // No "Today" label found (likely because today is a holiday), 
            // check first available date
            const firstDateTimeSlots = getFilteredTimeSlots(validDates[0].date, selectedOrderType);
            if (firstDateTimeSlots && firstDateTimeSlots.length > 0) {
              foundValidDate = true;
            }
          }
          
          // If no valid date found with slots, show warning
          if (!foundValidDate) {
            dispatchErrorMessageAsWarning({
              title: "Warning",
              message: "No slots for scheduled"
            });
            return; // Don't proceed with setting dates/times
          }
          
          // Set the selected date
          setSelectedDate(dateToSelect.date);
          
          // Also auto-select first time slot for this date
          const timeSlots = getFilteredTimeSlots(dateToSelect.date, selectedOrderType);
          if (timeSlots && timeSlots.length > 0) {
            setSelectedTime(timeSlots[0]);
            setAvailableTimeSlots(timeSlots);
          }
        } else {
          // No dates available at all
          dispatchErrorMessageAsWarning({
            title: "Warning",
            message: "No slots for scheduled"
          });
        }
      }
    }
  }, [selectedOrderType, asapLabel.available, scheduledAvailable, getAvailableDates, getFilteredTimeSlots, dispatchErrorMessageAsWarning, isOrderTypeAvailableForDate]); // Added missing dependencies

  // Handle radio change locally
  const handleRadioChange = useCallback((event) => {
    const newOrderTimeType = event.target.value;

    setOrderTimeType(newOrderTimeType);
    
    // If switching to SCHEDULED, check if today has no slots and auto-select next available date
    if (newOrderTimeType === "SCHEDULED" && selectedOrderType) {
      const allDates = getAvailableDates;
      if (allDates && allDates.length > 0) {
        // Filter dates to only include those that have valid slots for the selected order type
        const validDates = allDates.filter(date => {
          return isOrderTypeAvailableForDate(date.date, selectedOrderType);
        });
        
        if (validDates.length === 0) {
          return; // No valid dates for this order type
        }
        
        let dateToSelect = validDates[0]; // Default to first available date
        let foundValidDate = false;
        
        // Check if today has no time slots OR if today is a holiday
        const todayDate = validDates.find(d => d.label === "Today");
        if (todayDate) {
          const todayTimeSlots = getFilteredTimeSlots(todayDate.date, selectedOrderType);
          if (!todayTimeSlots || todayTimeSlots.length === 0) {
            // Today has no slots, find next available date
            for (let i = 1; i < validDates.length; i++) {
              const nextDate = validDates[i];
              const nextDateTimeSlots = getFilteredTimeSlots(nextDate.date, selectedOrderType);
              if (nextDateTimeSlots && nextDateTimeSlots.length > 0) {
                dateToSelect = nextDate;
                foundValidDate = true;
                break;
              }
            }
          } else {
            foundValidDate = true;
          }
        } else {
          // No "Today" label found (likely because today is a holiday), 
          // check first available date
          const firstDateTimeSlots = getFilteredTimeSlots(validDates[0].date, selectedOrderType);
          if (firstDateTimeSlots && firstDateTimeSlots.length > 0) {
            foundValidDate = true;
          }
        }
        
        // If no valid date found with slots, don't proceed
        if (!foundValidDate) {
          return; // Don't proceed with setting dates/times
        }
        
        // Set the selected date
        setSelectedDate(dateToSelect.date);
        
        // Also auto-select first time slot for this date
        const timeSlots = getFilteredTimeSlots(dateToSelect.date, selectedOrderType);
        if (timeSlots && timeSlots.length > 0) {
          setSelectedTime(timeSlots[0]);
          setAvailableTimeSlots(timeSlots);
        }
      } else {
        // No dates available at all - don't show warning here, handled in useEffect
      }
    }
    
    // Don't call callback here - wait for Continue button
  }, [selectedOrderType, getAvailableDates, getFilteredTimeSlots, isOrderTypeAvailableForDate]);

  // Handle date selection
  const handleDateChange = useCallback((date) => {
    setSelectedDate(date);
    
    // Check if the selected date has time slots, if not, find next available date
    if (date && selectedOrderType) {
      const timeSlots = getFilteredTimeSlots(date, selectedOrderType);
      if (!timeSlots || timeSlots.length === 0) {
        // Selected date has no slots, find next available date
        const allDates = getAvailableDates;
        if (allDates && allDates.length > 0) {
          // Filter dates to only include those that have valid slots for the selected order type
          const validDates = allDates.filter(d => {
            return isOrderTypeAvailableForDate(d.date, selectedOrderType);
          });
          
          const currentDateIndex = validDates.findIndex(d => d.date === date);
          if (currentDateIndex !== -1) {
            // Find next available date with slots
            for (let i = currentDateIndex + 1; i < validDates.length; i++) {
              const nextDate = validDates[i];
              const nextDateTimeSlots = getFilteredTimeSlots(nextDate.date, selectedOrderType);
              if (nextDateTimeSlots && nextDateTimeSlots.length > 0) {
                setSelectedDate(nextDate.date);
                setSelectedTime(nextDateTimeSlots[0]);
                setAvailableTimeSlots(nextDateTimeSlots);
                return; // Found next available date
              }
            }
          }
        }
        
        // If no next available date found, don't show warning here, handled in useEffect
      } else {
        // Selected date has slots, update time slots and auto-select first one
        setAvailableTimeSlots(timeSlots);
        setSelectedTime(timeSlots[0]);
      }
    }
    
    // Don't call callback here - wait for Continue button
    // This ensures consistency with handleTimeChange
  }, [selectedOrderType, getFilteredTimeSlots, getAvailableDates, isOrderTypeAvailableForDate]);

  // Centralized useEffect to handle warning when SCHEDULED is selected but no slots available
  useEffect(() => {
    if (orderTimeType === "SCHEDULED" && selectedOrderType) {
      const allDates = getAvailableDates;
      if (!allDates || allDates.length === 0) {
        // No dates available at all
        dispatchErrorMessageAsWarning({
          title: "Warning",
          message: "No slots for scheduled"
        });
        return;
      }
      
      // Filter dates to only include those that have valid slots for the selected order type
      const validDates = allDates.filter(date => {
        return isOrderTypeAvailableForDate(date.date, selectedOrderType);
      });
      
      // If no dates have valid slots for this order type, show warning
      if (validDates.length === 0) {
        dispatchErrorMessageAsWarning({
          title: "Warning",
          message: "No slots for scheduled"
        });
      }
    }
  }, [orderTimeType, selectedOrderType, getAvailableDates, isOrderTypeAvailableForDate, dispatchErrorMessageAsWarning]);

  // Check if no slots are available
  const noSlotsAvailable = !asapLabel.available && !scheduledAvailable;

  // State for showing unavailable order type alert
  const [showOrderTypeAlert, setShowOrderTypeAlert] = useState(false);
  const [blockedOrderType, setBlockedOrderType] = useState(null);
  // ✅ Track which order types have already shown alerts
  const [alertShownForOrderTypes, setAlertShownForOrderTypes] = useState(new Set());

  // Auto-switch to next available order type if current one has no slots
  useEffect(() => {
    if (selectedOrderType && noSlotsAvailable) {
      // ✅ Check if alert was already shown for this order type
      if (alertShownForOrderTypes.has(selectedOrderType)) {
        return; // Don't show alert again for the same order type
      }
      
      // Find the next order type that has available slots (if any)
      const currentIndex = orderTypes.findIndex(ot => ot.type === selectedOrderType);
      let nextOrderType = null;
      
      if (orderTypes && orderTypes.length > 0) {
        for (let i = 1; i <= orderTypes.length; i++) {
          const nextIndex = (currentIndex + i) % orderTypes.length;
          const candidateOrderType = orderTypes[nextIndex];
          
          // Avoid infinite loop when there is only one order type
          if (!candidateOrderType || candidateOrderType.type === selectedOrderType) {
            continue;
          }
          
          // Check if this order type has available slots
          const candidateAsapAvailable = isAsapAvailable(candidateOrderType.type);
          const candidateScheduledAvailable = (() => {
            if (!isScheduledAvailable || !candidateOrderType.type) {
              return false;
            }
            
            const dates = getAvailableDates;
            if (!dates || dates.length === 0) {
              return false;
            }
            
            // Check if any of the available dates have valid time slots for this order type
            for (const date of dates) {
              if (isOrderTypeAvailableForDate(date.date, candidateOrderType.type)) {
                return true;
              }
            }
            return false;
          })();
          
          if (candidateAsapAvailable || candidateScheduledAvailable) {
            nextOrderType = candidateOrderType;
            break;
          }
        }
      }
      
      // If we found a next available order type, auto-switch to it
      if (nextOrderType) {
        onOrderTypeClick(nextOrderType.type);
      } else {
        // No available order types found, show alert only if not shown before
        setBlockedOrderType(selectedOrderType);
        setShowOrderTypeAlert(true);
        // ✅ Mark this order type as having shown an alert
        setAlertShownForOrderTypes(prev => new Set(prev).add(selectedOrderType));
      }
    }
  }, [selectedOrderType, noSlotsAvailable, orderTypes, isAsapAvailable, isScheduledAvailable, getAvailableDates, isOrderTypeAvailableForDate, onOrderTypeClick, alertShownForOrderTypes]);

  // ✅ Reset alert tracking when order type changes
  useEffect(() => {
    if (selectedOrderType) {
      // Clear the alert shown tracking when order type changes
      setAlertShownForOrderTypes(new Set());
      setShowOrderTypeAlert(false);
    }
  }, [selectedOrderType]);

  // Check if current selection is valid for continue button
  const isSelectionValid = useMemo(() => {
    if (!selectedOrderType) return false;
    
    // Check if the selected order type has any available slots
    const asapOk = asapLabel.available;
    const scheduledOk = scheduledAvailable;
    
    return asapOk || scheduledOk;
  }, [selectedOrderType, asapLabel.available, scheduledAvailable]);
  return (
    <>
      {/* Title */}
      <div className={classes.modalTitleSection}>
        <Typography className={classes.modalTitle} variant="h1" align="center" paragraph>
          Select Order Type
        </Typography>
        <Divider />
      </div>

      {/* Order Type Buttons */}
      <div id="checkout-orderTypeForm" className={clsx(classes.container)}>
        {orderTypes.map((orderType, i) => (
          <div
            key={i}
            className={clsx(
              classes.btnNormal,
              orderType.type === selectedOrderType && classes.item
            )}
            onClick={() => onOrderTypeClick(orderType.type)}
          >
            <div className={classes.itemDiv}>
              <orderType.Icon
                className={clsx(
                  orderType.type === selectedOrderType
                    ? classes.itemIcon
                    : classes.normalIcon
                )}
              />
            </div>
            <Typography
              className={clsx(
                orderType.type === selectedOrderType
                  ? classes.itemText
                  : classes.normalText
              )}
              variant="caption"
            >
              {orderType.title}
            </Typography>
          </div>
        ))}
      </div>

      {/* Order Time Section */}
      {showOrderTime && selectedOrderType && (
        <div className={classes.modalTitleSection} style={{ marginTop: "16px" }}>
          <Typography className={classes.modalTitle} variant="h1" align="center" paragraph>
            Order Time
          </Typography>
        </div>
      )}

      {/* No Slots */}
      {(() => {
        // Check if ALL order types have no slots available
        const allOrderTypesHaveNoSlots = orderTypes.every(orderType => {
          const asapOk = isAsapAvailable(orderType.type);
          const scheduledOk = (() => {
            if (!isScheduledAvailable || !orderType.type) {
              return false;
            }
            
            const dates = getAvailableDates;
            if (!dates || dates.length === 0) {
              return false;
            }
            
            // Check if any of the available dates have valid time slots for this order type
            for (const date of dates) {
              if (isOrderTypeAvailableForDate(date.date, orderType.type)) {
                return true;
              }
            }
            return false;
          })();
          
          return !asapOk && !scheduledOk;
        });
        
        if (allOrderTypesHaveNoSlots) {
          return (
            <div className={classes.noSlotDiv}>
              <Typography className={classes.noSlotTxt}>
                No time slots available for any order type. Please come back tomorrow.
              </Typography>
            </div>
          );
        }
        
        // If only the current selected order type has no slots, show a different message
        if (selectedOrderType && noSlotsAvailable) {
          return (
            <div className={classes.noSlotDiv}>
              <Typography className={classes.noSlotTxt}>
                No time slots available for {selectedOrderType === 'DELIVERY' ? 'Delivery' : selectedOrderType === 'PICKUP' ? 'Pickup' : selectedOrderType}. 
                Auto-switching to next available option...
              </Typography>
            </div>
          );
        }
        
        return null;
      })()}

      {/* Order Time Section - Only show when there's a valid order type with available slots */}
      {selectedOrderType && isSelectionValid && (
        <div className={classes.timeSelectDiv}>
          {/* Radio Buttons */}
          <FormControl>
            <RadioGroup
              value={orderTimeType}
              onChange={handleRadioChange}
            >
              <FormControlLabel
                value="ASAP"
                className={classes.radioLabel}
                control={<Radio className={classes.radioBtn} />}
                label={asapLabel.label}
                disabled={!asapLabel.available}
              />
              <FormControlLabel
                value="SCHEDULED"
                className={classes.radioLabel}
                control={<Radio className={classes.radioBtn} />}
                label="Schedule for later"
                disabled={!scheduledAvailable}
              />
            </RadioGroup>
          </FormControl>

          {/* Date & Time Pickers */}
          {orderTimeType === "SCHEDULED" && (
            <>
              {/* Custom Date Selector */}
              <FormControl 
                variant="outlined" 
                className={`w-100 ${classes.dateTimeSelection}`}
                style={{ 
                  marginBottom: window.innerWidth <= 768 ? '20px' : '10px'
                }}
              >
                <Select
                  value={selectedDate || ""}
                  onChange={(e) => {
                    handleDateChange(e.target.value);
                  }}
                  displayEmpty
                >
                  <MenuItem value="">
                    <em>Select Date</em>
                  </MenuItem>
                  {availableDates.map((date, index) => (
                    <MenuItem key={index} value={date.date}>
                      {date.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TimePicker
                slots={availableTimeSlots}
                shouldFilter={false}
                isFromCheckout={false}
                orderTimeType={orderTimeType}
                className={`w-100 ${classes.dateTimeSelection}`}
                value={selectedTime}
                onChange={handleTimeChange}
                onClick={() => {}}
              />
            </>
          )}
        </div>
      )}

      {/* Continue */}
      <div className={classes.btnDiv}>
        <Button
          // disabled={
          //   !isSelectionValid || 
          //   (orderTimeType === "SCHEDULED" && (!selectedDate || !selectedTime))
          // }
          className={classes.continueBtn}
          onClick={() => {
            // Check if no slots are available - don't allow continue
            // if (!isSelectionValid) {
            //   return;
            // }
            
            // Check if no slots are available - save null values and proceed
            if (noSlotsAvailable && selectedOrderType) {
              dispatch(saveCheckoutForm({
                orderType: selectedOrderType,
                deliveryType: null,
                deliveryTime: null,
                deliveryDate: null
              }));
              
              // Call parent callbacks with null values
              onDateAndTimeSelect(selectedOrderType, null, null, null);
              onContinueClick(null, null);
              return;
            }
            
            // First, update Redux state with selected values
            const updateReduxState = () => {
              if (orderTimeType === "ASAP" && selectedOrderType) {
                // For ASAP: calculate ASAP time (current time + buffer time)
                const currentTime = DateTime.now();
                const bufferTime = getBufferTime(selectedOrderType); // Get buffer time for the order type
                const asapTime = currentTime.plus({ minutes: bufferTime }); // Add buffer time in minutes
                
                // Convert to UTC and format as ISO string for deliveryTime
                const asapTimeUTC = asapTime.toUTC();
                const asapTimeISO = asapTimeUTC.toISO();
                const today = currentTime.toFormat('yyyy-MM-dd');
                
                // Update Redux state directly
                dispatch(saveCheckoutForm({
                  orderType: selectedOrderType,
                  deliveryType: orderTimeType,
                  deliveryTime: asapTimeISO,
                  deliveryDate: today
                }));
                
                // Also call parent function for backward compatibility
                onDateAndTimeSelect(selectedOrderType, orderTimeType, asapTimeISO, today);
              } else if (orderTimeType === "SCHEDULED" && selectedOrderType && selectedDate && selectedTime) {
                // For SCHEDULED: deliveryType="SCHEDULED", orderType=selectedOrderType, deliveryTime=combined, deliveryDate=selectedDate
                const localTime = selectedTime.time; // This is "06:30" or "18:30" (24-hour format)
                
                // Parse the time to get hour and minute
                const [hour, minute] = localTime.split(':').map(Number);
                const [year, month, day] = selectedDate.split('-').map(Number);
                
                // Create a DateTime object in the local timezone (New Zealand)
                // This ensures that 6:30 AM means 6:30 AM in local time, not UTC
                const localDateTime = DateTime.local(year, month, day, hour, minute);
                
                // Convert to UTC for storage
                const utcDateTime = localDateTime.toUTC();
                const finalDateTime = utcDateTime.toISO();
                const finalDate = selectedDate;
                
                // Safety check: ensure the created time is in the future
                const now = DateTime.now();
                const deliveryTime = DateTime.fromISO(finalDateTime);
                
                let adjustedFinalDateTime = finalDateTime;
                let adjustedFinalDate = finalDate;
                
                if (deliveryTime <= now) {
                  const adjustedTime = deliveryTime.plus({ days: 1 });
                  adjustedFinalDateTime = adjustedTime.toISO();
                  
                  // Update the date as well
                  adjustedFinalDate = adjustedTime.toFormat('yyyy-MM-dd');
                }
                
                // Update Redux state directly
                dispatch(saveCheckoutForm({
                  orderType: selectedOrderType,
                  deliveryType: orderTimeType,
                  deliveryTime: adjustedFinalDateTime,
                  deliveryDate: adjustedFinalDate
                }));
                
                // Also call parent function for backward compatibility
                onDateAndTimeSelect(selectedOrderType, orderTimeType, adjustedFinalDateTime, adjustedFinalDate);
              }
            };
            
            // Update Redux state first
            updateReduxState();
            
            // Call onContinueClick with the selected values for immediate validation
            // This bypasses the need to wait for state updates
            if (orderTimeType === "ASAP" && selectedOrderType) {
              // For ASAP: pass current time + buffer
              const currentTime = DateTime.now();
              const bufferTime = getBufferTime(selectedOrderType);
              const asapTime = currentTime.plus({ minutes: bufferTime });
              const asapTimeUTC = asapTime.toUTC();
              const asapTimeISO = asapTimeUTC.toISO();
              const today = currentTime.toFormat('yyyy-MM-dd');
              
              onContinueClick(asapTimeISO, today);
            } else if (orderTimeType === "SCHEDULED" && selectedOrderType && selectedDate && selectedTime) {
              // For SCHEDULED: pass the calculated final values
              const localTime = selectedTime.time;
              
              // Parse the time to get hour and minute
              const [hour, minute] = localTime.split(':').map(Number);
              const [year, month, day] = selectedDate.split('-').map(Number);
              
              // Create a DateTime object in the local timezone (New Zealand)
              // This ensures that 6:30 AM means 6:30 AM in local time, not UTC
              const localDateTime = DateTime.local(year, month, day, hour, minute);
              
              // Convert to UTC for storage
              const utcDateTime = localDateTime.toUTC();
              const finalDateTime = utcDateTime.toISO();
              const finalDate = selectedDate;
              
              onContinueClick(finalDateTime, finalDate);
            }
          }}
        >
          Continue
        </Button>
      </div>
      
      {/* Alert for blocked order type */}
      <SpecialAlert
        open={showOrderTypeAlert}
        title={`${blockedOrderType} is currently unavailable. This order type has no ASAP and scheduled slots available at the moment.`}
        handleClose={() => setShowOrderTypeAlert(false)}
        isBtnRight={true}
      />
    </>
  );
};

export default SelectOrderTypeUI;
